import { useCallback, useRef } from 'react';

import { TIME_PRECISION } from '../utils/constants';
import { constrainSelectionTime } from '../utils/selectionCalculations';
import { VideoSelection, roundToPrecision } from '../utils/videoUtils';

interface UseMoveDragProps {
  selection: VideoSelection;
  videoDuration: number;
  stripWidth: number;
  onSelectionMove: (startTime: number) => void;
  onSeek?: (time: number) => void;
  isPlaying?: boolean;
  onPlayPause?: () => void;
}

interface UseMoveDragReturn {
  handleMoveStart: () => void;
  handleMoveDrag: (deltaX: number) => void;
  handleMoveStop: () => void;
}

/** Manages selection move drag operations with constraints and video scrubbing. */
export function useMoveDrag({
  selection,
  videoDuration,
  stripWidth,
  onSelectionMove,
  onSeek,
  isPlaying = false,
  onPlayPause,
}: UseMoveDragProps): UseMoveDragReturn {
  const wasPlayingBeforeDragRef = useRef<boolean>(false);
  const dragStartRef = useRef<{
    initialStartTime: number;
  } | null>(null);

  const pixelDeltaToTimeDelta = useCallback(
    (deltaPixels: number): number => {
      if (stripWidth <= 0) return 0;
      return (deltaPixels * videoDuration) / stripWidth;
    },
    [videoDuration, stripWidth]
  );

  const handleMoveStart = useCallback(() => {
    wasPlayingBeforeDragRef.current = isPlaying;

    if (isPlaying && onPlayPause) {
      onPlayPause();
    }

    dragStartRef.current = {
      initialStartTime: selection.startTime,
    };
  }, [isPlaying, onPlayPause, selection.startTime]);

  const handleMoveDrag = useCallback(
    (deltaX: number) => {
      if (!dragStartRef.current) return;

      const timeDelta = pixelDeltaToTimeDelta(deltaX);
      const newStartTime = constrainSelectionTime(
        dragStartRef.current.initialStartTime + timeDelta,
        selection.duration,
        videoDuration
      );

      const roundedStartTime = roundToPrecision(newStartTime, TIME_PRECISION);

      onSelectionMove(roundedStartTime);
      onSeek?.(roundedStartTime);
    },
    [
      pixelDeltaToTimeDelta,
      selection.duration,
      videoDuration,
      onSelectionMove,
      onSeek,
    ]
  );

  const handleMoveStop = useCallback(() => {
    if (wasPlayingBeforeDragRef.current && onPlayPause) {
      onPlayPause();
    }

    dragStartRef.current = null;
  }, [onPlayPause]);

  return {
    handleMoveStart,
    handleMoveDrag,
    handleMoveStop,
  };
}
